import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import {
    DEFAULT_SKY_SVG,
    DEFAULT_CITY_SVG,
    DEFAULT_TRACK_SVG,
    getDynamicRunnerSvg,
    getDynamicWinnerSvg,
    getInflatableArchSvg,
    preloadImageUrls,
    resolveAssetUrl
} from './CarreraContinuaAssets';

/**
 * Normaliza y obtiene el nombre visual de la zona
 */
const getZoneDisplayName = (zoneId, runners = []) => {
    if (!zoneId) return 'Zona';
    const safeId = String(zoneId).trim().toLowerCase();

    if (Array.isArray(runners) && runners.length > 0) {
        const found = runners.find(r =>
            String(r.zoneId || r.zoneIdentifier || '').trim().toLowerCase() === safeId
        );
        if (found && (found.displayName || found.zoneName || found.label)) {
            return found.displayName || found.zoneName || found.label;
        }
    }

    if (safeId.startsWith('zona')) {
        const num = safeId.replace('zona', '').trim();
        return `Zona ${num}`;
    }

    return zoneId.charAt(0).toUpperCase() + zoneId.slice(1);
};

/**
 * Obtiene el color temático de la zona
 */
const getZoneColor = (zoneId, runners = [], defaultIndex = 0) => {
    const DEFAULT_COLORS = ['#dc2626', '#2563eb', '#16a34a', '#eab308', '#9333ea', '#0891b2'];
    if (!zoneId) return DEFAULT_COLORS[defaultIndex % DEFAULT_COLORS.length];
    const safeId = String(zoneId).trim().toLowerCase();

    if (Array.isArray(runners) && runners.length > 0) {
        const found = runners.find(r =>
            String(r.zoneId || r.zoneIdentifier || '').trim().toLowerCase() === safeId
        );
        if (found && (found.themeColorHex || found.color)) {
            return found.themeColorHex || found.color;
        }
    }

    let hash = 0;
    for (let i = 0; i < safeId.length; i++) {
        hash = (hash << 5) - hash + safeId.charCodeAt(i);
        hash |= 0;
    }
    return DEFAULT_COLORS[Math.abs(hash + defaultIndex) % DEFAULT_COLORS.length];
};

/**
 * COMPONENTE DE JUEGO PARA PANTALLA GIGANTE: CARRERA CONTINUA (HU-06)
 * Motor Parallax Zero-Canvas (DOM/CSS3 GPU), carriles dinámicos, arcos con espuma y telemetría espacial.
 */
export const CarreraContinuaGame = ({
    currentMatchState: propMatchState,
    theme: propTheme,
    config: propConfig,
    roomId
}) => {
    // =========================================================================
    // 1. CONSOLIDACIÓN DE ESTADO Y TEMA
    // =========================================================================
    const matchState = propMatchState || {};
    const rawPhase = matchState.phase || propConfig?.phase || 'WAITING';
    const phase = String(rawPhase).toUpperCase();

    // Referencia de tema persistente en memoria para evitar pérdida durante telemetría SignalR
    const cachedThemeRef = useRef(null);

    // Deserializar tema desde props, config o snapshot en tiempo real
    const theme = useMemo(() => {
        const candidate = (propTheme && typeof propTheme === 'object' && Object.keys(propTheme).length > 0)
            ? propTheme
            : (matchState.theme && typeof matchState.theme === 'object' && Object.keys(matchState.theme).length > 0)
                ? matchState.theme
                : (propConfig && typeof propConfig === 'object' && Object.keys(propConfig).length > 0)
                    ? propConfig
                    : null;

        let resolved = candidate;

        if (!resolved || Object.keys(resolved).length === 0 || (!resolved.defaultArchImageUrl && !resolved.archImageUrl && !resolved.skyBackgroundUrl && !resolved.trackBackgroundUrl && !resolved.checkpointsConfigJson && !resolved.runnersConfigJson)) {
            if (matchState.themeSnapshotJson) {
                try {
                    const parsed = typeof matchState.themeSnapshotJson === 'string'
                        ? JSON.parse(matchState.themeSnapshotJson)
                        : matchState.themeSnapshotJson;
                    if (parsed && typeof parsed === 'object') resolved = { ...(resolved || {}), ...parsed };
                } catch (e) {
                    console.warn('[CarreraContinuaGame] Error al parsear themeSnapshotJson:', e);
                }
            }
            if (propConfig?.datosDinamica) {
                try {
                    const parsed = typeof propConfig.datosDinamica === 'string'
                        ? JSON.parse(propConfig.datosDinamica)
                        : propConfig.datosDinamica;
                    if (parsed && typeof parsed === 'object') resolved = { ...(resolved || {}), ...parsed };
                } catch (e) {}
            }
        }

        if (resolved && typeof resolved === 'object' && Object.keys(resolved).length > 0) {
            cachedThemeRef.current = resolved;
            return resolved;
        }

        return cachedThemeRef.current || resolved || propConfig || {};
    }, [propTheme, matchState.theme, matchState.themeSnapshotJson, propConfig]);

    // Extracción de assets de tema
    const skyBackgroundUrl = resolveAssetUrl(theme.skyBackgroundUrl) || DEFAULT_SKY_SVG;
    const cityBackgroundUrl = resolveAssetUrl(theme.cityBackgroundUrl) || DEFAULT_CITY_SVG;
    const trackBackgroundUrl = resolveAssetUrl(theme.trackBackgroundUrl) || DEFAULT_TRACK_SVG;
    const brandLogoUrl = resolveAssetUrl(theme.brandLogoUrl) || null;
    const sponsorLogoUrl = resolveAssetUrl(theme.sponsorLogoUrl) || null;
    const totalKilometers = Number(matchState.totalKilometers || theme.totalKilometers || 5);
    const currentLeadingKm = Number(matchState.currentLeadingKilometer !== undefined ? matchState.currentLeadingKilometer : 0);
    const fontFamily = theme.fontFamily || 'Montserrat, sans-serif';

    // Deserializar checkpoints
    const checkpoints = useMemo(() => {
        let list = null;
        if (Array.isArray(matchState.checkpoints) && matchState.checkpoints.length > 0) {
            list = matchState.checkpoints;
        } else if (Array.isArray(theme.checkpoints) && theme.checkpoints.length > 0) {
            list = theme.checkpoints;
        } else if (theme.checkpointsConfigJson) {
            try {
                const parsed = typeof theme.checkpointsConfigJson === 'string'
                    ? JSON.parse(theme.checkpointsConfigJson)
                    : theme.checkpointsConfigJson;
                if (Array.isArray(parsed) && parsed.length > 0) list = parsed;
            } catch (e) {}
        }

        const globalArch = theme.defaultArchImageUrl || theme.archImageUrl || matchState.defaultArchImageUrl || matchState.archImageUrl || '';

        if (list && list.length > 0) {
            return list.map((cp, idx) => {
                const archImg = cp.archImageUrl || cp.bannerImageUrl || globalArch;
                return {
                    ...cp,
                    kilometer: Number(cp.kilometer || cp.kilometerIndex || (idx + 1)),
                    label: cp.label || cp.labelText || (idx + 1 === totalKilometers ? '¡META FINAL!' : `KM ${idx + 1}`),
                    archImageUrl: archImg,
                    bannerImageUrl: archImg,
                    foamColorHex: cp.foamColorHex || '#00e5ff',
                    hasFoam: cp.hasFoam !== undefined ? cp.hasFoam : true,
                    sponsorLogoUrl: cp.sponsorLogoUrl || sponsorLogoUrl || ''
                };
            });
        }

        // Generar hitos por defecto para los kilómetros
        const foamColors = ['#00e5ff', '#ff007f', '#00ff66', '#ffbb00', '#ffd700', '#9900ff', '#ff3300'];
        const generated = [];
        for (let i = 1; i <= totalKilometers; i++) {
            generated.push({
                kilometer: i,
                kilometerIndex: i,
                label: i === totalKilometers ? '¡META FINAL!' : `KM ${i}`,
                labelText: i === totalKilometers ? '¡META FINAL!' : `KM ${i}`,
                foamColorHex: foamColors[(i - 1) % foamColors.length],
                hasFoam: true,
                archImageUrl: globalArch,
                bannerImageUrl: globalArch,
                sponsorLogoUrl: sponsorLogoUrl || ''
            });
        }
        return generated;
    }, [matchState.checkpoints, theme.checkpoints, theme.checkpointsConfigJson, theme.defaultArchImageUrl, theme.archImageUrl, matchState.defaultArchImageUrl, matchState.archImageUrl, totalKilometers, sponsorLogoUrl]);

    // Deserializar configuración de corredores / zonas
    const runnersConfig = useMemo(() => {
        if (Array.isArray(matchState.runners) && matchState.runners.length > 0) {
            return matchState.runners;
        }
        if (Array.isArray(theme.runners) && theme.runners.length > 0) {
            return theme.runners;
        }
        if (theme.runnersConfigJson) {
            try {
                const parsed = typeof theme.runnersConfigJson === 'string'
                    ? JSON.parse(theme.runnersConfigJson)
                    : theme.runnersConfigJson;
                if (Array.isArray(parsed) && parsed.length > 0) return parsed;
            } catch (e) {}
        }
        if (cachedThemeRef.current?.runners && Array.isArray(cachedThemeRef.current.runners)) {
            return cachedThemeRef.current.runners;
        }
        return [];
    }, [matchState.runners, theme.runners, theme.runnersConfigJson]);

    // Zonas activas de la carrera
    const zonesProgress = useMemo(() => {
        if (Array.isArray(matchState.zonesProgress) && matchState.zonesProgress.length > 0) {
            return matchState.zonesProgress;
        }
        if (runnersConfig && runnersConfig.length > 0) {
            return runnersConfig.map((r, idx) => ({
                zoneIdentifier: r.zoneIdentifier || r.zoneId || r.displayName || r.zoneName || `zona${idx + 1}`,
                totalTaps: 0,
                scorePerCapita: 0,
                visualPositionPercentage: 0
            }));
        }
        // Fallback a 2 zonas para coincidir con la sala actual
        return [
            { zoneIdentifier: 'zona1', totalTaps: 0, scorePerCapita: 0, visualPositionPercentage: 0 },
            { zoneIdentifier: 'zona2', totalTaps: 0, scorePerCapita: 0, visualPositionPercentage: 0 }
        ];
    }, [matchState.zonesProgress, runnersConfig]);

    // =========================================================================
    // 2. PRECARGA DE IMÁGENES EN MEMORIA (ImagePreloader)
    // =========================================================================
    useEffect(() => {
        const urlsToPreload = [
            skyBackgroundUrl,
            cityBackgroundUrl,
            trackBackgroundUrl,
            brandLogoUrl,
            sponsorLogoUrl,
            theme.defaultArchImageUrl,
            theme.archImageUrl
        ];

        checkpoints.forEach(cp => {
            if (cp.archImageUrl) urlsToPreload.push(cp.archImageUrl);
            if (cp.bannerImageUrl) urlsToPreload.push(cp.bannerImageUrl);
            if (cp.sponsorLogoUrl) urlsToPreload.push(cp.sponsorLogoUrl);
        });

        runnersConfig.forEach(r => {
            if (r.avatarRunUrl) urlsToPreload.push(r.avatarRunUrl);
            if (r.avatarWinUrl) urlsToPreload.push(r.avatarWinUrl);
            if (r.avatarUrl) urlsToPreload.push(r.avatarUrl);
            if (r.spriteUrl) urlsToPreload.push(r.spriteUrl);
        });

        preloadImageUrls(urlsToPreload);
    }, [skyBackgroundUrl, cityBackgroundUrl, trackBackgroundUrl, brandLogoUrl, sponsorLogoUrl, theme.defaultArchImageUrl, theme.archImageUrl, checkpoints, runnersConfig]);

    // =========================================================================
    // 3. MOTOR PARALLAX ZERO-CANVAS (DOM/CSS3 GPU A 60 FPS - 2 CAPAS)
    // =========================================================================
    const landscapeLayerRef = useRef(null);
    const trackLayerRef = useRef(null);

    const animationFrameRef = useRef(null);
    const lastTimestampRef = useRef(null);
    const offsetLandscapeRef = useRef(0);
    const offsetTrackRef = useRef(0);
    const currentSpeedRef = useRef(0);

    const BASE_TRACK_SPEED_PCT_PER_SEC = 24.0; // Velocidad de desplazamiento horizontal

    useEffect(() => {
        const updateParallax = (timestamp) => {
            if (!lastTimestampRef.current) {
                lastTimestampRef.current = timestamp;
            }
            const deltaTimeSec = Math.min(0.1, (timestamp - lastTimestampRef.current) / 1000.0);
            lastTimestampRef.current = timestamp;

            let targetSpeed = 0;
            if (phase === 'RUNNING') {
                targetSpeed = BASE_TRACK_SPEED_PCT_PER_SEC;
                // Aceleración gradual
                currentSpeedRef.current += (targetSpeed - currentSpeedRef.current) * Math.min(1, deltaTimeSec * 4);
            } else if (phase === 'FINISHED') {
                // Desaceleración suave en meta con fricción
                currentSpeedRef.current *= Math.pow(0.92, deltaTimeSec * 60);
                if (currentSpeedRef.current < 0.05) currentSpeedRef.current = 0;
            } else {
                currentSpeedRef.current = 0;
            }

            if (currentSpeedRef.current > 0) {
                const spd = currentSpeedRef.current;
                // Capa 1 (Paisaje/Cielo+Casas): 0.35x de velocidad. Multiplicamos por 0.5 porque el strip mide 200%.
                offsetLandscapeRef.current = (offsetLandscapeRef.current + (spd * 0.35 * deltaTimeSec) * 0.5) % 50;
                // Capa 2 (Pista de atletismo): 1.0x de velocidad completa
                offsetTrackRef.current = (offsetTrackRef.current + (spd * 1.0 * deltaTimeSec) * 0.5) % 50;

                if (landscapeLayerRef.current) {
                    landscapeLayerRef.current.style.transform = `translate3d(-${offsetLandscapeRef.current}%, 0, 0)`;
                }
                if (trackLayerRef.current) {
                    trackLayerRef.current.style.transform = `translate3d(-${offsetTrackRef.current}%, 0, 0)`;
                }
            }

            animationFrameRef.current = requestAnimationFrame(updateParallax);
        };

        animationFrameRef.current = requestAnimationFrame(updateParallax);

        return () => {
            if (animationFrameRef.current) {
                cancelAnimationFrame(animationFrameRef.current);
            }
        };
    }, [phase]);

    // =========================================================================
    // 4. SISTEMA DE ARCOS DE KILOMETRAJE Y DETONACIÓN DE ESPUMA CSS SUTIL
    // =========================================================================
    const [foamBursts, setFoamBursts] = useState([]);
    const triggeredArchIndexesSet = useMemo(() => {
        const list = matchState.triggeredArchIndexes || [];
        return new Set(list.map(Number));
    }, [matchState.triggeredArchIndexes]);

    const previousTriggeredRef = useRef(new Set());
    const runnerArchTriggeredRef = useRef(new Set());

    // Disparar ráfaga de espuma sutil y elegante localizada en el arco
    const triggerFoamBurstAtArch = useCallback((archIndex, foamColor) => {
        const burstId = `foam-${Date.now()}-${Math.random()}`;
        const particlesCount = 18; // Reducido a 18 partículas para mayor sutileza y elegancia
        const generatedParticles = [];

        for (let i = 0; i < particlesCount; i++) {
            const nozzleType = Math.random();
            let startX = 0;
            let startY = 0;
            let tx = 0;
            let ty = 0;

            if (nozzleType < 0.45) {
                // Boquilla izquierda: Disparo hacia adentro con niebla suave
                startX = -130 + (Math.random() * 20 - 10);
                startY = 240 + (Math.random() * 40);
                const angle = (Math.random() * Math.PI * 0.4) - (Math.PI * 0.2);
                const dist = 50 + Math.random() * 110;
                tx = Math.cos(angle) * dist;
                ty = -Math.sin(angle) * dist + (Math.random() * 40 - 20);
            } else if (nozzleType < 0.90) {
                // Boquilla derecha: Disparo hacia adentro con niebla suave
                startX = 130 + (Math.random() * 20 - 10);
                startY = 240 + (Math.random() * 40);
                const angle = Math.PI - ((Math.random() * Math.PI * 0.4) - (Math.PI * 0.2));
                const dist = 50 + Math.random() * 110;
                tx = Math.cos(angle) * dist;
                ty = -Math.sin(angle) * dist + (Math.random() * 40 - 20);
            } else {
                // Boquilla superior / viga: Niebla sutil hacia abajo
                startX = (Math.random() * 220) - 110;
                startY = 370 + (Math.random() * 20);
                tx = (Math.random() * 70) - 35;
                ty = 50 + (Math.random() * 100);
            }

            const size = 12 + Math.random() * 20;
            const duration = 1.4 + Math.random() * 1.0;
            const delay = Math.random() * 0.25;

            generatedParticles.push({
                id: i,
                startX,
                startY,
                tx,
                ty,
                size,
                duration,
                delay,
                scale: 0.7 + Math.random() * 0.4
            });
        }

        setFoamBursts(prev => [...prev, {
            id: burstId,
            archIndex,
            foamColorHex: foamColor || '#00e5ff',
            particles: generatedParticles
        }]);

        setTimeout(() => {
            setFoamBursts(prev => prev.filter(b => b.id !== burstId));
        }, 2500);
    }, []);

    // Escuchar nuevos hitos activados por telemetría
    useEffect(() => {
        triggeredArchIndexesSet.forEach(idx => {
            if (!previousTriggeredRef.current.has(idx)) {
                previousTriggeredRef.current.add(idx);
                const cp = checkpoints[idx];
                const color = cp?.foamColorHex || '#00e5ff';
                triggerFoamBurstAtArch(idx, color);
            }
        });
    }, [triggeredArchIndexesSet, checkpoints, triggerFoamBurstAtArch]);

    // Resetear hitos y posiciones al reiniciar la carrera
    useEffect(() => {
        if (phase === 'WAITING') {
            offsetLandscapeRef.current = 0;
            offsetTrackRef.current = 0;
            currentSpeedRef.current = 0;
            if (landscapeLayerRef.current) {
                landscapeLayerRef.current.style.transform = 'translate3d(0, 0, 0)';
            }
            if (trackLayerRef.current) {
                trackLayerRef.current.style.transform = 'translate3d(0, 0, 0)';
            }
            previousTriggeredRef.current.clear();
            runnerArchTriggeredRef.current.clear();
            setFoamBursts([]);
        }
    }, [phase]);

    // =========================================================================
    // 5. CÁLCULO DE LÍDERES, PROGRESO Y POSICIÓN VISUAL
    // =========================================================================
    const leadingVisualPosition = useMemo(() => {
        if (!zonesProgress || zonesProgress.length === 0) return 0;
        const maxPos = Math.max(...zonesProgress.map(z => z.visualPositionPercentage || 0));
        return Math.min(100, Math.max(0, maxPos));
    }, [zonesProgress]);

    const leadingZoneIdentifier = useMemo(() => {
        if (!zonesProgress || zonesProgress.length === 0) return null;
        const sorted = [...zonesProgress].sort((a, b) => (b.visualPositionPercentage || 0) - (a.visualPositionPercentage || 0));
        return sorted[0]?.zoneIdentifier || null;
    }, [zonesProgress]);

    // Determinación de Ganadores / Podio
    const winnerZone = matchState.winnerZone || matchState.winner || null;
    const isTie = Boolean(matchState.isTie || (winnerZone && (winnerZone === 'tie' || winnerZone.startsWith('tie_partial:'))));

    // Disparar ráfagas de espuma cuando un corredor cruza físicamente por debajo de un arco
    useEffect(() => {
        if (phase !== 'RUNNING') return;

        const RUNNER_START_PCT = 8;
        const RUNNER_RANGE_PCT = 72;
        const spreadFactor = Math.max(4.0, totalKilometers * 1.15);

        zonesProgress.forEach((zoneItem, zIdx) => {
            const visualPos = zoneItem.visualPositionPercentage || 0;
            const runnerLeftPct = RUNNER_START_PCT + ((visualPos / 100) * RUNNER_RANGE_PCT);

            checkpoints.forEach((cp, archIdx) => {
                const targetKmPct = (cp.kilometer / totalKilometers) * 100;
                const targetLeaderScreenPct = RUNNER_START_PCT + ((targetKmPct / 100) * RUNNER_RANGE_PCT);
                const archDelta = targetKmPct - leadingVisualPosition;
                const archLeftPct = targetLeaderScreenPct + (archDelta * spreadFactor);

                const runnerKey = `${zoneItem.zoneIdentifier || zIdx}-arch-${archIdx}`;
                if (!runnerArchTriggeredRef.current.has(runnerKey)) {
                    // Si el corredor pasa por debajo del arco (distancia < 7% o cruce confirmado)
                    if (Math.abs(runnerLeftPct - archLeftPct) < 7 || (runnerLeftPct >= archLeftPct && archLeftPct > 0 && archLeftPct < 95)) {
                        runnerArchTriggeredRef.current.add(runnerKey);
                        const color = cp?.foamColorHex || '#00e5ff';
                        triggerFoamBurstAtArch(archIdx, color);
                    }
                }
            });
        });
    }, [zonesProgress, leadingVisualPosition, checkpoints, totalKilometers, phase, triggerFoamBurstAtArch]);

    const finalRankings = useMemo(() => {
        if (Array.isArray(matchState.finalResults) && matchState.finalResults.length > 0) {
            return matchState.finalResults;
        }
        return [...zonesProgress].sort((a, b) => (b.scorePerCapita || 0) - (a.scorePerCapita || 0) || (b.totalTaps || 0) - (a.totalTaps || 0));
    }, [matchState.finalResults, zonesProgress]);

    // Confetti para celebración de victoria
    const confettiPieces = useMemo(() => {
        if (phase !== 'FINISHED') return [];
        const colors = ['#facc15', '#ef4444', '#3b82f6', '#10b981', '#ec4899', '#8b5cf6', '#ffffff'];
        const pieces = [];
        for (let i = 0; i < 75; i++) {
            pieces.push({
                id: i,
                left: Math.random() * 100,
                color: colors[i % colors.length],
                size: 10 + Math.random() * 18,
                duration: 2.5 + Math.random() * 2.5,
                delay: Math.random() * 2,
                rotation: Math.random() * 360
            });
        }
        return pieces;
    }, [phase]);

    // Helper para avatar de corredor según fase, zona e índice de carril
    const getRunnerAvatar = (zoneId, isWinner = false, laneIndex = 0) => {
        const safeId = String(zoneId || '').trim().toLowerCase();
        
        let runnerCfg = runnersConfig.find(r => {
            const rId = String(r.zoneId || r.zoneIdentifier || '').trim().toLowerCase();
            const rName = String(r.zoneName || r.displayName || r.label || '').trim().toLowerCase();
            return (rId && (rId === safeId || safeId.includes(rId) || rId.includes(safeId))) ||
                   (rName && (rName === safeId || safeId.includes(rName) || rName.includes(safeId)));
        });

        // Si no se encontró por coincidencia de texto, asociar por número de carril (carril 0 -> corredor 0, etc.)
        if (!runnerCfg && Array.isArray(runnersConfig) && runnersConfig[laneIndex]) {
            runnerCfg = runnersConfig[laneIndex];
        }

        if (isWinner) {
            const winUrl = runnerCfg?.avatarWinUrl || runnerCfg?.spriteUrl || runnerCfg?.avatarRunUrl || runnerCfg?.avatarUrl;
            if (winUrl) return resolveAssetUrl(winUrl);
            return getDynamicWinnerSvg(getZoneColor(zoneId, runnersConfig, laneIndex), getZoneDisplayName(zoneId, runnersConfig));
        }

        const runUrl = runnerCfg?.avatarRunUrl || runnerCfg?.avatarUrl || runnerCfg?.spriteUrl;
        if (runUrl) return resolveAssetUrl(runUrl);

        return getDynamicRunnerSvg(getZoneColor(zoneId, runnersConfig, laneIndex), getZoneDisplayName(zoneId, runnersConfig));
    };

    return (
        <div
            className="ccgame"
            style={{
                fontFamily: fontFamily
            }}
        >
            {/* ============================================================= */}
            {/* CAPAS DE MOTOR PARALLAX DOM ZERO-CANVAS (2 CAPAS UNIFICADAS) */}
            {/* ============================================================= */}
            
            {/* CAPA 1: PAISAJE COMPLETO / CIELO + CASAS/ESTADIO (0.35x) */}
            <div className="ccgame__parallax-layer ccgame__parallax-layer--city ccgame__parallax-layer--landscape">
                <div ref={landscapeLayerRef} className="ccgame__parallax-strip">
                    <div className="ccgame__parallax-tile" style={{ backgroundImage: `url(${cityBackgroundUrl || skyBackgroundUrl})` }} />
                    <div className="ccgame__parallax-tile" style={{ backgroundImage: `url(${cityBackgroundUrl || skyBackgroundUrl})` }} />
                </div>
            </div>

            {/* CAPA 2: SUELO / PISTA DE ATLETISMO (1.0x) */}
            <div className="ccgame__parallax-layer ccgame__parallax-layer--track">
                <div ref={trackLayerRef} className="ccgame__parallax-strip">
                    <div className="ccgame__parallax-tile" style={{ backgroundImage: `url(${trackBackgroundUrl})` }} />
                    <div className="ccgame__parallax-tile" style={{ backgroundImage: `url(${trackBackgroundUrl})` }} />
                </div>
            </div>

            {/* ============================================================= */}
            {/* TELEMETRÍA ESPACIAL SUPERIOR (SIN CRONÓMETRO NUMÉRICO) */}
            {/* ============================================================= */}
            <header className="ccgame__hud">
                {/* LOGOS DE MARCA / PATROCINADOR */}
                {/* <div className="ccgame__hud-logos">
                    {brandLogoUrl && (
                        <img src={brandLogoUrl} alt="Club" className="ccgame__hud-logo" />
                    )}
                    {sponsorLogoUrl && (
                        <img src={sponsorLogoUrl} alt="Sponsor" className="ccgame__hud-logo" />
                    )}
                </div> */}

                {/* CARTEL SUPERIOR KILOMÉTRICO DE VANGUARDIA */}
                <div className="ccgame__hud-telemetry mx-auto">
                    <div className="ccgame__km-card">
                        <div className="ccgame__km-value">
                            <div className='d-block '>
                                {brandLogoUrl ? (
                                    <img src={brandLogoUrl} alt="Club" className="ccgame__hud-logo" style={{ maxWidth: '300px', marginBottom: '10px' }} />
                                ) : sponsorLogoUrl ? (
                                    <img src={sponsorLogoUrl} alt="Sponsor" className="ccgame__hud-logo" style={{ maxWidth: '300px', marginBottom: '10px' }} />
                                ) : (
                                    <img src="/img/page/xolos-five.png" alt="Logo" style={{ maxWidth: '300px', marginBottom: '10px' }} />
                                )}
                            </div>
                            <div className='d-block text-center'>
                                <span className="ccgame__km-current">KM {Math.min(totalKilometers, Math.max(0, currentLeadingKm))}</span>
                                <span className="ccgame__km-total"> DE {totalKilometers}</span>
                            </div>
                        </div>
                    </div>

                    {/* BARRA DE PROGRESO DE KILOMETRAJE */}
                    <div className="ccgame__progress-container">
                        <div className="ccgame__progress-track">
                            <div
                                className="ccgame__progress-fill"
                                style={{ width: `${Math.min(100, Math.max(0, leadingVisualPosition))}%` }}
                            />
                            {/* Hitos en la barra */}
                            {checkpoints.map((cp, idx) => {
                                const stepPct = ((idx + 1) / totalKilometers) * 100;
                                const isPassed = leadingVisualPosition >= stepPct;
                                return (
                                    <div
                                        key={idx}
                                        className={`ccgame__progress-node ${isPassed ? 'ccgame__progress-node--passed' : ''}`}
                                        style={{ left: `${stepPct}%` }}
                                        title={cp.label || `Km ${idx + 1}`}
                                    >
                                        <span className="ccgame__progress-node-text">{idx + 1}k</span>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </div>
            </header>

            {/* ============================================================= */}
            {/* CARRILES Y CORREDORES DINÁMICOS EN LA PISTA */}
            {/* ============================================================= */}
            <main className="ccgame__track-stage">
                <div
                    className="ccgame__lanes"
                    style={{
                        gridTemplateRows: `repeat(${zonesProgress.length}, 1fr)`
                    }}
                >
                    {zonesProgress.map((zoneItem, index) => {
                        const zoneId = zoneItem.zoneIdentifier || `zona${index + 1}`;
                        const zoneName = getZoneDisplayName(zoneId, runnersConfig);
                        const zoneColor = getZoneColor(zoneId, runnersConfig, index);
                        const visualPos = zoneItem.visualPositionPercentage || 0;

                        // Mapeo seguro de posición horizontal (8% a 80% del ancho de pantalla)
                        const RUNNER_START_PCT = 8;
                        const RUNNER_RANGE_PCT = 72;
                        const screenLeftPct = RUNNER_START_PCT + ((visualPos / 100) * RUNNER_RANGE_PCT);

                        // Comprobar si esta zona es ganadora en fase FINISHED
                        const isThisWinner = phase === 'FINISHED' && (
                            winnerZone === zoneId ||
                            (winnerZone && winnerZone.toLowerCase() === zoneId.toLowerCase()) ||
                            (winnerZone && winnerZone.startsWith('tie_partial:') && winnerZone.toLowerCase().includes(zoneId.toLowerCase())) ||
                            (winnerZone === 'tie')
                        );

                        const avatarSrc = getRunnerAvatar(zoneId, isThisWinner, index);

                        return (
                            <div
                                key={zoneId}
                                className={`ccgame__lane ccgame__lane--${index + 1}`}
                            >
                                {/* Corredor en Movimiento */}
                                <div
                                    className={`ccgame__runner ${phase === 'RUNNING' ? 'ccgame__runner--trotting' : ''} ${isThisWinner ? 'ccgame__runner--winner' : ''}`}
                                    style={{
                                        left: `${screenLeftPct}%`,
                                        '--zone-color': zoneColor
                                    }}
                                >
                                    {/* Estela de Velocidad / Aura de Color */}
                                    {phase === 'RUNNING' && (
                                        <div
                                            className="ccgame__runner-trail"
                                            style={{
                                                background: `linear-gradient(270deg, ${zoneColor} 0%, transparent 100%)`
                                            }}
                                        />
                                    )}

                                    {/* Sombra Dinámica en el Suelo */}
                                    <div className="ccgame__runner-shadow" />

                                    {/* Avatar Gráfico del Corredor */}
                                    <img
                                        src={avatarSrc}
                                        alt={zoneName}
                                        className="ccgame__runner-avatar"
                                    />
                                </div>
                            </div>
                        );
                    })}
                </div>

                {/* ========================================================= */}
                {/* ARCOS DE KILOMETRAJE Y META CON RÁFAGAS DE ESPUMA LOCALIZADAS */}
                {/* ========================================================= */}
                <div className="ccgame__arches-container">
                    {checkpoints.map((cp, idx) => {
                        const targetKmPct = (cp.kilometer / totalKilometers) * 100;
                        const isFinishArch = cp.kilometer === totalKilometers;
                        const isTriggered = triggeredArchIndexesSet.has(idx);

                        // Sincronización exacta: cuando el progreso llega a este Km, el arco coincide con el líder
                        const RUNNER_START_PCT = 8;
                        const RUNNER_RANGE_PCT = 72;
                        const targetLeaderScreenPct = RUNNER_START_PCT + ((targetKmPct / 100) * RUNNER_RANGE_PCT);
                        const archDelta = targetKmPct - leadingVisualPosition;
                        const spreadFactor = Math.max(4.0, totalKilometers * 1.15);
                        const archLeftPct = targetLeaderScreenPct + (archDelta * spreadFactor);

                        // Si el arco ya pasó muy a la izquierda o aún no entra, no saturar renderizado
                        if (archLeftPct < -30 || archLeftPct > 150) return null;

                        const customArchUrl = cp.archImageUrl || cp.bannerImageUrl || theme.defaultArchImageUrl || theme.archImageUrl || matchState.defaultArchImageUrl || matchState.archImageUrl;
                        const resolvedArch = resolveAssetUrl(customArchUrl);
                        const archSrc = resolvedArch || getInflatableArchSvg(
                            cp.label || `KM ${cp.kilometer}`,
                            cp.foamColorHex || '#00e5ff',
                            resolveAssetUrl(cp.sponsorLogoUrl || sponsorLogoUrl || ''),
                            isFinishArch
                        );

                        const burstsForThisArch = foamBursts.filter(b => b.archIndex === idx);

                        return (
                            <div
                                key={idx}
                                className={`ccgame__arch ${isFinishArch ? 'ccgame__arch--finish' : ''} ${isTriggered ? 'ccgame__arch--active' : ''}`}
                                style={{
                                    left: `${archLeftPct}%`
                                }}
                            >
                                <img
                                    src={archSrc}
                                    alt={cp.label || `Arco Km ${cp.kilometer}`}
                                    className="ccgame__arch-img"
                                />
                                {/* {isFinishArch && (
                                    <div className="ccgame__finish-banner">
                                        🏁 LÍNEA DE META FINAL 🏁
                                    </div>
                                )} */}

                                {/* Ráfagas de espuma proyectadas directamente desde la estructura de este arco */}
                                {burstsForThisArch.map(burst => (
                                    <div key={burst.id} className="ccgame__arch-foam-container">
                                        {burst.particles.map(p => (
                                            <div
                                                key={p.id}
                                                className="ccgame__foam-particle"
                                                style={{
                                                    left: `calc(50% + ${p.startX}px)`,
                                                    bottom: `${p.startY}px`,
                                                    backgroundColor: burst.foamColorHex,
                                                    width: `${p.size}px`,
                                                    height: `${p.size}px`,
                                                    boxShadow: `0 0 16px ${burst.foamColorHex}, inset 0 0 8px #ffffff`,
                                                    '--tx': `${p.tx}px`,
                                                    '--ty': `${p.ty}px`,
                                                    '--scale': p.scale,
                                                    animationDuration: `${p.duration}s`,
                                                    animationDelay: `${p.delay}s`
                                                }}
                                            />
                                        ))}
                                    </div>
                                ))}
                            </div>
                        );
                    })}
                </div>
            </main>

            {/* ============================================================= */}
            {/* OVERLAYS DE FASE: CUENTA REGRESIVA Y CINEMÁTICA DE VICTORIA */}
            {/* ============================================================= */}

            {/* FASE: CUENTA REGRESIVA (3, 2, 1, ¡YA!) */}
            {phase === 'COUNTDOWN' && (
                <div className="ccgame__countdown-overlay">
                    <div className="ccgame__countdown-modal">
                        <span className="ccgame__countdown-sub">¡EN SUS MARCAS!</span>
                        <div className="ccgame__countdown-num">
                            {matchState.secondsRemaining !== undefined && matchState.secondsRemaining > 0
                                ? matchState.secondsRemaining
                                : '¡YA!'}
                        </div>
                        <span className="ccgame__countdown-pulse" />
                    </div>
                </div>
            )}

            {/* FASE: CINEMÁTICA DE META Y VICTORIA (FINISHED) */}
            {phase === 'FINISHED' && (
                <div className="ccgame__victory-overlay">
                    {/* LLUVIA DE CONFETI DE ESTADIO */}
                    <div className="ccgame__confetti-wrapper">
                        {confettiPieces.map(c => (
                            <div
                                key={c.id}
                                className="ccgame__confetti-piece"
                                style={{
                                    left: `${c.left}%`,
                                    backgroundColor: c.color,
                                    width: `${c.size}px`,
                                    height: `${c.size * 0.6}px`,
                                    animationDuration: `${c.duration}s`,
                                    animationDelay: `${c.delay}s`,
                                    transform: `rotate(${c.rotation}deg)`
                                }}
                            />
                        ))}
                    </div>

                    {/* BANNER DE CAMPEÓN / PODIO */}
                    <div className="ccgame__victory-card">
                        <img src="/img/page/xolos-five.png" alt="Logo" style={{ maxWidth: '450px', marginBottom: '1em' }} />

                        <h2 className="ccgame__victory-title">
                            {isTie ? (
                                <>¡EMPATE ÉPICO!</>
                            ): (
                                <>
                                    <span className='d-block'>El ganador(a) es...</span>
                                    <span className='d-block mt-2'>{getZoneDisplayName(winnerZone, runnersConfig).toUpperCase()}</span>
                                </>
                            )}

                            {/* {isTie
                                ? '¡EMPATE ÉPICO!'
                                : `¡El ganador(a) es ${getZoneDisplayName(winnerZone, runnersConfig).toUpperCase()}!`} */}
                        </h2>

                        <div className="ccgame__victory-trophy mt-2">
                            {isTie ? '🤝' : '🏆'}
                        </div>

                        {/* <p className="ccgame__victory-subtitle">
                            {isTie
                                ? 'Las zonas han cruzado la meta con igualdad absoluta de esfuerzo.'
                                : `¡Gran victoria en los ${totalKilometers} Kilómetros de Carrera Continua!`}
                        </p> */}

                        {/* TABLA DE RESULTADOS DE ZONAS */}
                        {/* <div className="ccgame__leaderboard">
                            {finalRankings.map((r, idx) => {
                                const zName = getZoneDisplayName(r.zoneIdentifier, runnersConfig);
                                const zColor = getZoneColor(r.zoneIdentifier, runnersConfig, idx);
                                const isWinnerRow = idx === 0 || (isTie && (!winnerZone || winnerZone === 'tie'));

                                return (
                                    <div
                                        key={r.zoneIdentifier || idx}
                                        className={`ccgame__leaderboard-row ${isWinnerRow ? 'ccgame__leaderboard-row--winner' : ''}`}
                                    >
                                        <div className="ccgame__leaderboard-rank">
                                            {idx === 0 ? '🥇' : (idx === 1 ? '🥈' : (idx === 2 ? '🥉' : `#${idx + 1}`))}
                                        </div>
                                        <div
                                            className="ccgame__leaderboard-zone"
                                            style={{ color: zColor }}
                                        >
                                            <span
                                                className="ccgame__leaderboard-dot"
                                                style={{ backgroundColor: zColor }}
                                            />
                                            <span>{zName}</span>
                                        </div>
                                        <div className="ccgame__leaderboard-taps">
                                            <strong>{r.totalTaps || 0}</strong> taps
                                        </div>
                                    </div>
                                );
                            })}
                        </div> */}
                    </div>
                </div>
            )}

            {/* PIE DE PÁGINA */}
            <footer className="ccgame__footer">
                <p className="ccgame__credits" style={{ color: '#ffffff', fontSize: '1.6em' }}>Hecho con ♥ por Interaktia</p>
            </footer>
        </div>
    );
};

export default CarreraContinuaGame;
